import React, { useEffect, useState } from 'react';
import { Container } from '../../components/styleComponents';
import Loader from '../../components/Loader';
import { IUsuario } from 'src/interfaces/index';
import api from 'src/service/api';
import { toast } from 'react-toastify';

import {
    Toolbar,
    TotalRegistros,
    FormContainer,
    FormRow,
    FormLabel,
    FormInput,
    CheckboxContainer,
    TableContainer,
    Table,
    TableRow,
    Pagination,
    SearchContainer,
    Divider,
} from './style';
import {
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaSave,
    FaTrash,
    FaEraser,
} from 'react-icons/fa';
import validarCPF from 'src/utils/validaCpf';

const Usuarios: React.FC = () => {
    const [usuarios, setUsuarios] = useState<IUsuario[]>([]);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const pageSize = 10;

    const [usuarioSelecionado, setUsuarioSelecionado] =
        useState<IUsuario | null>(null);

    const [form, setForm] = useState<Partial<IUsuario>>({
        usuAtivo: true,
    });

    const modoInsercao = usuarioSelecionado === null;

    const [filtroNome, setFiltroNome] = useState('');

    const usuariosFiltrados = usuarios.filter((usuario) =>
    usuario.usuNome
        ?.toLowerCase()
        .includes(filtroNome.toLowerCase()),
);

    const updateForm = (
        field: keyof IUsuario,
        value: string | number | boolean,
    ) => {
        setForm((current) => ({
            ...current,
            [field]: value,
        }));
    };

    const carregarUsuarios = async () => {
        try {
            setLoading(true);
            const token = sessionStorage.getItem('token');
            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;
            const response = await api.get('/usuarios');

            setUsuarios(response.data);
        } catch (error) {
            console.error(
                'Erro ao carregar suários',
                error,
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        carregarUsuarios();
    }, []);

    const limparFormulario = () => {
        setUsuarioSelecionado(null);

        setForm({
            usuAtivo: true,
        });

        setFiltroNome('');
        setPage(1);
    };

    const selecionarUsuario = (usuario: IUsuario) => {
        setUsuarioSelecionado(usuario);

        setForm({
            usuId: usuario.usuId,
            usuNome: usuario.usuNome,
            usuLogin: usuario.usuLogin,
            usuEmail: usuario.usuEmail,
            usuMatricula: usuario.usuMatricula,
            usuCi: usuario.usuCi,
            usuCpf: usuario.usuCpf,
            usuAtivo: usuario.usuAtivo,
            usuTipo: usuario.usuTipo,
            usuManual: usuario.usuManual,
        });
    };

    const salvar = async () => {

        if (!validarFormulario()) {
            return;
        }

        try {
            const token = sessionStorage.getItem('token');

            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;

            const payload = {
                usuNome: form.usuNome,
                usuLogin: form.usuLogin,
                usuEmail: form.usuEmail,
                usuMatricula: form.usuMatricula,
                usuCi: form.usuCi,
                usuCpf: form.usuCpf,
                usuAtivo: form.usuAtivo,
                usuTipo: form.usuTipo,
                usuManual: form.usuManual,
            };

            if (modoInsercao) {
                await api.post('/usuarios/inclui', {
                    usuId: form.usuId,
                    ...payload,
                });
            } else {
                await api.put(`/usuarios/${form.usuId}`, payload);
            }
            await carregarUsuarios();
            limparFormulario();
            toast.success('Usuário salvo com sucesso.');
        } catch (error) {
            toast.error('Erro ao salvar usuário.');
            console.error('Erro ao salvar usuário', error);
        }
    };

    const excluir = async () => {
        if (!usuarioSelecionado) {
            return;
        }
        const confirmou = window.confirm(
            `Deseja excluir o usuário "${usuarioSelecionado.usuNome}"?`,
        );

        if (!confirmou) {
            return;
        }

        try {
            const token = sessionStorage.getItem('token');
            api.defaults.headers.Authorization = `Bearer ${token}`;
            api.defaults.headers.chaValor = process.env.REACT_APP_CHAVE_API;
            await api.delete('/usuarios', {
                data: {
                    ids: [usuarioSelecionado.usuId],
                },
            });

            await carregarUsuarios();
            limparFormulario();
            setPage(1);
            toast.success('Usuário excluído com sucesso.');
        } catch (error) {
            toast.error('Erro ao excluir usuário.');
            console.error(
                'Erro ao excluir usuário',
                error,
            );
        }
    };

    const totalPages = Math.ceil(
        usuariosFiltrados.length / pageSize,
    );

    const usuariosPaginados = usuariosFiltrados.slice(
        (page - 1) * pageSize,
        page * pageSize,
    );

    if (loading) {
        return <Loader />;
    }

    const somenteNumeros = (valor: string): string => {
        return valor.replace(/\D/g, '');
    };

    const validarFormulario = (): boolean => {
        const campos: string[] = [];

        if (!form.usuId) campos.push('Usu_id');
        if (!form.usuNome?.trim()) campos.push('Nome');
        if (!form.usuLogin?.trim()) campos.push('Login');
        if (!form.usuEmail?.trim()) campos.push('Email');
        if (!form.usuMatricula) campos.push('Matrícula');
        if (!form.usuCi?.trim()) campos.push('CI');
        if (!form.usuCpf?.trim()) campos.push('CPF');
        if (form.usuCpf && !validarCPF(form.usuCpf)) {
            alert('CPF inválido.');
            return false;
        }
        if (!form.usuTipo?.trim()) campos.push('Tipo');
        if (form.usuTipo && !['efetivo', 'comissionado', 'estagiario', 'nao_classificado'].includes(form.usuTipo)
        ) {
            alert('O campo Tipo deve conter apenas efetivo, comissionado, estagiario ou nao_classificado.');
            return false;
        }

        if (campos.length > 0) {
            alert(
                `Os seguintes campos são obrigatórios:\n\n${campos.join('\n')}`,
            );
            return false;
        }

        return true;
    };

    return (
        <Container>
            <h2>Usuários</h2>

            <Toolbar>
                <button
                    type="button"
                    onClick={salvar}
                >
                    <FaSave />
                    {modoInsercao ? 'Incluir' : 'Atualizar'}
                </button>

                <button
                    type="button"
                    onClick={limparFormulario}
                >
                    <FaEraser />
                    Limpar campos
                </button>

                <button
                    type="button"
                    disabled={!usuarioSelecionado}
                    onClick={excluir}
                >
                    <FaTrash />
                    Excluir
                </button>

                <TotalRegistros>
                    {modoInsercao ? 'Modo Inclusão' : `Modo Edição - Usuário ${form.usuCpf}`} - Total de registros:{' '}{usuarios.length}
                </TotalRegistros>
            </Toolbar>

            <FormContainer>
                <FormRow>
                    <FormLabel htmlFor="usuId">
                        Usu_id
                    </FormLabel>

                    <FormInput
                        id="usuId"
                        width="100px"
                        type="text"
                        disabled={!modoInsercao}
                        value={form.usuId ?? ''}
                        onChange={(e) => {
                            const valor = somenteNumeros(e.target.value);

                            updateForm(
                                'usuId',
                                valor === '' ? '' : Number(valor),
                            );
                        }}
                    />
                    <FormLabel htmlFor="usuNome" style={{ marginLeft: '5px' }}>
                        Nome
                    </FormLabel>

                    <FormInput
                        id="usuNome"
                        width="750px"
                        type="text"
                        value={form.usuNome ?? ''}
                        onChange={(e) =>
                            updateForm(
                                'usuNome',
                                e.target.value,
                            )
                        }
                    />
                    <FormLabel htmlFor="usuLogin" style={{ marginLeft: '5px' }}>
                        Login
                    </FormLabel>

                    <FormInput
                        id="usuLogin"
                        width="350px"
                        type="text"
                        value={form.usuLogin ?? ''}
                        onChange={(e) =>
                            updateForm(
                                'usuLogin',
                                e.target.value,
                            )
                        }
                    />
                </FormRow>

                <FormRow>
                    <FormLabel
                        htmlFor="usuMatricula"
                    >
                        Matrícula
                    </FormLabel>

                    <FormInput
                        id="usuMatricula"
                        width="80px"
                        type="text"
                        value={form.usuMatricula ?? ''}
                        onChange={(e) => {
                            const valor = somenteNumeros(e.target.value);

                            updateForm(
                                'usuMatricula',
                                valor === '' ? '' : Number(valor),
                            );
                        }}
                    />
                    <FormLabel htmlFor="usuEmail" style={{ marginLeft: '25px' }}>
                        Email
                    </FormLabel>

                    <FormInput
                        id="usuEmail"
                        width="605px"
                        type="text"
                        value={form.usuEmail ?? ''}
                        onChange={(e) =>
                            updateForm(
                                'usuEmail',
                                e.target.value,
                            )
                        }
                    />
                </FormRow>

                <FormRow>
                    <FormLabel htmlFor="usuCi">
                        Ci
                    </FormLabel>

                    <FormInput
                        id="usuCi"
                        width="250px"
                        type="text"
                        value={form.usuCi ?? ''}
                        onChange={(e) =>
                            updateForm(
                                'usuCi',
                                e.target.value,
                            )
                        }
                    />

                    <FormLabel htmlFor="usuCpf">
                        Cpf
                    </FormLabel>

                    <FormInput
                        id="usuCpf"
                        width="130px"
                        type="text"
                        value={form.usuCpf ?? ''}
                        onChange={(e) =>
                            updateForm(
                                'usuCpf',
                                e.target.value,
                            )
                        }
                    />
                    <FormLabel
                        htmlFor="usuTipo"
                        style={{
                            width: '80px',
                            minWidth: '80px',
                            marginLeft: '5px',
                        }}
                    >
                        Tipo
                    </FormLabel>

                    <FormInput
                        id="usuTipo"
                        width="245px"
                        type="text"
                        value={form.usuTipo ?? ''}
                        onChange={(e) => {
                            const valor = e.target.value;
                            updateForm('usuTipo', valor);
                        }}
                    />
                    </FormRow>
                    <FormRow>
                    <FormLabel>
                        Ativo
                    </FormLabel>

                    <CheckboxContainer>
                        <input
                            type="checkbox"
                            checked={form.usuAtivo ?? false}
                            onChange={(e) =>
                                updateForm(
                                    'usuAtivo',
                                    e.target.checked,
                                )
                            }
                        />
                    </CheckboxContainer>

                    <FormLabel
                        style={{
                            width: '60px',
                            minWidth: '60px',
                            marginLeft: '20px',
                        }}
                    >
                        Manual
                    </FormLabel>

                    <CheckboxContainer>
                        <input
                            type="checkbox"
                            checked={form.usuManual ?? false}
                            onChange={(e) =>
                                updateForm(
                                    'usuManual',
                                    e.target.checked,
                                )
                            }
                        />
                    </CheckboxContainer>
                </FormRow>
            </FormContainer>
            <Divider/>
            <SearchContainer>
                <label htmlFor="pesquisaNome">
                    Pesquisar Nome
                </label>

                <input
                    id="pesquisaNome"
                    type="text"
                    value={filtroNome}
                    onChange={(e) => {
                        setFiltroNome(e.target.value);
                        setPage(1);
                    }}
                />

                <span>
                    Encontrados: {usuariosFiltrados.length}
                </span>
            </SearchContainer>

            <TableContainer>
                <Table>
                    <thead>
                        <tr>
                            <th>Usu_id</th>
                            <th>Nome</th>
                            <th>Login</th>
                            <th>Email</th>
                            <th>Matricula</th>
                            <th>Ci</th>
                            <th>Cpf</th>
                            <th>Ativo</th>
                            <th>Tipo</th>
                            <th>Manual</th>
                        </tr>
                    </thead>

                    <tbody>
                        {usuariosPaginados.map(
                            (usuario) => (
                                <TableRow
                                    key={
                                        usuario.usuId
                                    }
                                    selected={
                                        usuarioSelecionado?.usuId ===
                                        usuario.usuId
                                    }
                                    onClick={() =>
                                        selecionarUsuario(
                                            usuario,
                                        )
                                    }
                                >
                                    <td>
                                        {
                                            usuario.usuId
                                        }
                                    </td>
                                    <td>
                                        {
                                            usuario.usuNome
                                        }
                                    </td>
                                    <td>
                                        {
                                            usuario.usuLogin
                                        }
                                    </td>
                                    <td>
                                        {
                                            usuario.usuEmail
                                        }
                                    </td>
                                    <td>
                                        {
                                            usuario.usuMatricula
                                        }
                                    </td>
                                    <td>
                                        {
                                            usuario.usuCi
                                        }
                                    </td>
                                    <td>
                                        {
                                            usuario.usuCpf
                                        }
                                    </td>
                                    <td>
                                        {usuario.usuAtivo
                                            ? 'Sim'
                                            : 'Não'}
                                    </td>
                                    <td>
                                        {
                                            usuario.usuTipo
                                        }
                                    </td>
                                    <td>
                                        {usuario.usuManual
                                            ? 'Sim'
                                            : 'Não'}
                                    </td>
                                </TableRow>
                            ),
                        )}
                    </tbody>
                </Table>
            </TableContainer>

            <Pagination>
                <button
                    type="button"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                >
                    <FaAngleDoubleLeft />
                    Primeira
                </button>

                <button
                    type="button"
                    disabled={page === 1}
                    onClick={() =>
                        setPage((current) => current - 1)
                    }
                >
                    <FaAngleLeft />
                    Anterior
                </button>

                <span>Página {page} de {totalPages || 1}</span>

                <button
                    type="button"
                    disabled={
                        page >= totalPages ||
                        totalPages === 0
                    }
                    onClick={() =>
                        setPage((current) => current + 1)
                    }
                >
                    <FaAngleRight />
                    Próxima
                </button>

                <button
                    type="button"
                    disabled={
                        page >= totalPages ||
                        totalPages === 0
                    }
                    onClick={() => setPage(totalPages)}
                >
                    <FaAngleDoubleRight />
                    Última
                </button>
            </Pagination>
        </Container>
    );
};

export default Usuarios;
